import {
  createDelegatedKeyWalletAndNonce,
  DelegatedKeyCreationParams,
} from '@katanaperps/katana-perps-sdk';
import AES from 'crypto-js/aes';
import encoderUtf8 from 'crypto-js/enc-utf8';
import { BaseWallet, Signer, Wallet } from 'ethers';

import { WALLET_PRIVATE_KEY_CHARS } from 'config/constants';

import { getAnonymousRPCProvider } from './utils';

import type { DelegatedKeyAuthorization } from 'reduxState/delegatedKey/authorizationReducer';
import type { IWallet } from 'reduxState/wallet/walletReducer';

function encryptDelegatedKey(
  privateKey: string,
  personalWalletSecret: string
): string {
  if (!personalWalletSecret) {
    throw new Error('No personal wallet secret provided');
  }

  const delegatedKeyEncrypted = AES.encrypt(
    privateKey,
    personalWalletSecret
  ).toString();

  return delegatedKeyEncrypted;
}

function decryptDelegatedKey(
  delegatedKeyEncrypted: string,
  personalWalletSecret: string
) {
  const privateKey = AES.decrypt(
    delegatedKeyEncrypted,
    personalWalletSecret
  ).toString(encoderUtf8);
  return { privateKey };
}

async function decryptDelegatedKeyPrivateKey(
  wallet: DelegatedKeyAuthorization,
  custodyWallet: IWallet,
  personalWalletSecret: string
) {
  const { walletAddress: custodyWalletAddress } = custodyWallet;
  if (!personalWalletSecret) {
    throw new Error(
      'Delegated Key Signers are only valid when we have a known personal wallet secret'
    );
  }

  if (custodyWalletAddress !== wallet.custodyWalletAddress) {
    throw new Error(
      'Custody Wallet Address mismatch for delegated key, can not get signer.'
    );
  }

  const { privateKey } = decryptDelegatedKey(
    wallet.delegatedKeyEncrypted,
    personalWalletSecret
  );

  // PK is 64 Bytes + 2 Bytes for "0x"
  if (privateKey.length !== WALLET_PRIVATE_KEY_CHARS) {
    throw new Error('Session Private key is not valid.');
  }
  return privateKey;
}

export async function getDelegatedKeySigner(
  wallet: DelegatedKeyAuthorization,
  custodyWallet: IWallet,
  personalWalletSecret: string
): Promise<Signer> {
  const privateKey = await decryptDelegatedKeyPrivateKey(
    wallet,
    custodyWallet,
    personalWalletSecret
  );
  return new Wallet(privateKey, getAnonymousRPCProvider());
}

type InitialDelegatedKeyShape = Omit<
  DelegatedKeyAuthorization,
  'delegatedKeyEncrypted'
>;

/**
 * TODO: update desc here
 * Generates an initial delegated key which must be signed and have its mising
 * `delegatedKeyEncrypted` and `personalWalletSecret` properties added to it.
 *
 * It returns a tuple which can be used to request a signature from the user and
 * once complete, sending the personalWalletSecret will return a fully formed
 * {@link DelegatedKeyAuthorization} object.
 *
 * @param wallet The custody wallet
 */
export async function generateDelegatedKey(nonce: string): Promise<
  DelegatedKeyCreationParams & {
    delegatedHDWallet: BaseWallet;
  }
> {
  const [delegatedWallet] = createDelegatedKeyWalletAndNonce();

  return {
    delegatedKey: delegatedWallet.address,
    delegatedNonce: nonce,
    delegatedHDWallet: delegatedWallet,
  };
}

export function getCompleteDelegatedKey({
  custodyWalletAddress,
  delegatedWalletHD,
  personalWalletSecret,
}: {
  custodyWalletAddress: string;
  delegatedWalletHD: BaseWallet;
  personalWalletSecret: string;
}) {
  const delegatedKey: InitialDelegatedKeyShape = {
    createdTime: Date.now(),
    walletType: 'delegatedKey' as const,
    custodyWalletAddress,
    walletAddress: delegatedWalletHD.address,
    walletAddressOriginal: delegatedWalletHD.address,

    privateKeyPersonalEncryption: 'personal1',
  };
  // we dont have this until after associate
  return {
    ...delegatedKey,
    delegatedKeyEncrypted: encryptDelegatedKey(
      delegatedWalletHD.privateKey,
      personalWalletSecret
    ),
  };
}
